import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Eye, Users, Monitor, Smartphone, Tablet, Activity, TrendingUp, TrendingDown, ShoppingBag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
];

const TABS = [
  { key: 'overview', label: 'Vista General' },
  { key: 'products', label: 'Productos' },
  { key: 'devices', label: 'Dispositivos' },
];

const DEVICE_COLORS = {
  mobile: '#C9A962',
  desktop: '#8DB600',
  tablet: '#6366f1',
};

const DEVICE_LABELS = {
  mobile: 'Móvil',
  desktop: 'Escritorio',
  tablet: 'Tablet',
};

const DEVICE_ICONS = {
  mobile: Smartphone,
  desktop: Monitor,
  tablet: Tablet,
};

const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
  },
  labelStyle: { color: '#C9A962' },
};

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function percentChange(current, previous) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState('products');
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [pageStats, setPageStats] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [devices, setDevices] = useState(null);
  const [realtime, setRealtime] = useState(null);

  const token = localStorage.getItem('davocado-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/analytics/overview`, { headers }),
        axios.get(`${API}/analytics/page-stats?period=${period}`, { headers }),
        axios.get(`${API}/analytics/product-stats?period=${period}`, { headers }),
        axios.get(`${API}/analytics/devices?period=${period}`, { headers }),
        axios.get(`${API}/analytics/realtime`, { headers }),
      ];
      const [overviewRes, pageRes, productRes, deviceRes, realtimeRes] = await Promise.all(requests);
      setOverview(overviewRes.data);
      setPageStats(pageRes.data);
      setProductStats(productRes.data);
      setDevices(deviceRes.data);
      setRealtime(realtimeRes.data);
    } catch (error) {
      toast.error('Error cargando analíticas');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/analytics/realtime`, { headers });
        setRealtime(res.data);
      } catch {}
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-none" />
          ))}
        </div>
        <div className="h-80 skeleton rounded-none" />
      </div>
    );
  }

  const todayChange = percentChange(overview?.today_views || 0, overview?.yesterday_views || 0);

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif text-white mb-2">Analíticas</h1>
          <p className="text-white/60">Estadísticas de visitas y productos</p>
        </div>

        <div className="flex items-center gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                period === p.value
                  ? 'bg-[#C9A962] text-black'
                  : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-3 text-sm font-medium transition-colors relative ${
              activeTab === tab.key
                ? 'text-[#C9A962]'
                : 'text-white/60 hover:text-white'
            }`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <motion.div
                layoutId="analytics-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9A962]"
              />
            )}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <OverviewTab
          overview={overview}
          pageStats={pageStats}
          realtime={realtime}
          todayChange={todayChange}
        />
      )}

      {activeTab === 'products' && (
        <ProductsTab productStats={productStats} />
      )}

      {activeTab === 'devices' && (
        <DevicesTab devices={devices} />
      )}
    </div>
  );
}

function OverviewTab({ overview, pageStats, realtime, todayChange }) {
  const statCards = [
    {
      title: 'Visitantes Hoy',
      value: overview?.today_unique_visitors || 0,
      subtitle: `${overview?.today_views || 0} vistas`,
      change: todayChange,
      icon: Eye,
      color: 'text-[#C9A962]',
      bgColor: 'bg-[#C9A962]/10',
    },
    {
      title: 'Esta Semana',
      value: overview?.week_unique_visitors || 0,
      subtitle: `${overview?.week_views || 0} vistas`,
      icon: TrendingUp,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Este Mes',
      value: overview?.month_unique_visitors || 0,
      subtitle: `${overview?.month_views || 0} vistas`,
      icon: Users,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Activos Ahora',
      value: realtime?.active_visitors || 0,
      subtitle: 'últimos 30 min',
      icon: Activity,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
      pulse: true,
    },
  ];

  const totalViews = pageStats?.pages?.reduce((sum, p) => sum + p.views, 0) || 1;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{stat.title}</p>
                    <p className="text-2xl font-semibold text-white">{stat.value.toLocaleString()}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-white/40 text-sm">{stat.subtitle}</p>
                      {stat.change !== undefined && (
                        <span className={`text-xs font-medium ${stat.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {stat.change >= 0 ? '+' : ''}{stat.change}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor} relative`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    {stat.pulse && realtime?.active_visitors > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-serif">Visitas Diarias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pageStats?.daily_views || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 12 }} />
                  <Tooltip
                    {...chartTooltipStyle}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Legend
                    wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="views"
                    name="Vistas"
                    stroke="#C9A962"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, fill: '#C9A962' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="unique_visitors"
                    name="Visitantes Únicos"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, fill: '#22c55e' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-serif">Páginas Más Visitadas</CardTitle>
          </CardHeader>
          <CardContent>
            {pageStats?.pages?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left text-white/60 font-medium">Página</th>
                      <th className="py-3 px-4 text-right text-white/60 font-medium">Vistas</th>
                      <th className="py-3 px-4 text-right text-white/60 font-medium hidden sm:table-cell">Visitantes Únicos</th>
                      <th className="py-3 px-4 text-right text-white/60 font-medium hidden md:table-cell">% del Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageStats.pages.map((page, index) => (
                      <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white font-medium">{page.page_title || page.page}</p>
                            <p className="text-white/40 text-xs">{page.page}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-white">{page.views.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right text-white/60 hidden sm:table-cell">{page.unique_visitors.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right text-[#C9A962] hidden md:table-cell">
                          {((page.views / totalViews) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-white/40 text-center py-8">No hay datos de páginas aún</p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function ProductsTab({ productStats }) {
  const navigate = useNavigate();
  const products = productStats?.products || [];
  const topProduct = products[0];
  const totalProductViews = products.reduce((sum, p) => sum + p.views, 0);
  const avgViews = products.length > 0 ? Math.round(totalProductViews / products.length) : 0;
  const top10 = products.slice(0, 10);

  const statCards = [
    {
      title: 'Producto Más Visto',
      value: topProduct?.product_name || '-',
      subtitle: topProduct ? `${topProduct.views} vistas` : 'Sin datos',
      icon: Eye,
      color: 'text-[#C9A962]',
      bgColor: 'bg-[#C9A962]/10',
      isText: true,
    },
    {
      title: 'Total Vistas de Productos',
      value: totalProductViews,
      subtitle: `${products.length} productos vistos`,
      icon: ShoppingBag,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Promedio Vistas/Producto',
      value: avgViews,
      subtitle: 'por producto',
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 mr-3">
                    <p className="text-white/60 text-sm mb-1">{stat.title}</p>
                    <p className={`font-semibold text-white ${stat.isText ? 'text-lg truncate' : 'text-2xl'}`}>
                      {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </p>
                    <p className="text-white/40 text-sm mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor} flex-shrink-0`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {top10.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif">Top 10 Productos Más Vistos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={top10}
                    layout="vertical"
                    margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                    <XAxis type="number" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="product_name"
                      width={150}
                      stroke="rgba(255,255,255,0.3)"
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                    />
                    <Tooltip {...chartTooltipStyle} />
                    <Bar dataKey="views" name="Vistas" fill="#C9A962" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-serif">Detalle por Producto</CardTitle>
          </CardHeader>
          <CardContent>
            {products.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left text-white/60 font-medium w-12">#</th>
                      <th className="py-3 px-4 text-left text-white/60 font-medium">Producto</th>
                      <th className="py-3 px-4 text-left text-white/60 font-medium hidden sm:table-cell">Categoría</th>
                      <th className="py-3 px-4 text-right text-white/60 font-medium">Vistas</th>
                      <th className="py-3 px-4 text-right text-white/60 font-medium hidden sm:table-cell">Visitantes Únicos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => (
                      <tr key={product.product_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4">
                          <span className="w-7 h-7 rounded-lg bg-[#C9A962]/10 flex items-center justify-center text-[#C9A962] text-xs font-medium">
                            {index + 1}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => navigate(`/admin/productos?edit=${product.product_id}`)}
                            className="text-white font-medium hover:text-[#C9A962] hover:underline transition-colors text-left"
                          >
                            {product.product_name}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-white/60 hidden sm:table-cell">{product.product_category || '-'}</td>
                        <td className="py-3 px-4 text-right text-white">{product.views.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right text-white/60 hidden sm:table-cell">{product.unique_visitors.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-white/40 text-center py-8">No hay datos de productos aún</p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function DevicesTab({ devices }) {
  const deviceData = devices?.devices || [];
  const pieData = deviceData.map((d) => ({
    name: DEVICE_LABELS[d.type] || d.type,
    value: d.count,
    color: DEVICE_COLORS[d.type] || '#888',
    type: d.type,
    percentage: d.percentage,
  }));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif">Distribución por Dispositivo</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={3}
                        dataKey="value"
                        nameKey="name"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value, name) => [`${value.toLocaleString()} visitas`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay datos de dispositivos aún</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-[#121212] border-white/10 h-full">
            <CardHeader>
              <CardTitle className="text-white font-serif">Desglose</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {deviceData.map((device, index) => {
                  const Icon = DEVICE_ICONS[device.type] || Monitor;
                  const color = DEVICE_COLORS[device.type] || '#888';
                  const label = DEVICE_LABELS[device.type] || device.type;

                  return (
                    <motion.div
                      key={device.type}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded" style={{ backgroundColor: `${color}20` }}>
                            <Icon className="w-5 h-5" style={{ color }} />
                          </div>
                          <span className="text-white font-medium">{label}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-white font-semibold">{device.percentage}%</span>
                          <span className="text-white/40 text-sm ml-2">({device.count.toLocaleString()})</span>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${device.percentage}%` }}
                          transition={{ duration: 0.8, delay: index * 0.15 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: color }}
                        />
                      </div>
                    </motion.div>
                  );
                })}

                {deviceData.length === 0 && (
                  <p className="text-white/40 text-center py-8">No hay datos de dispositivos aún</p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
